// components/MapView.tsx
import { ErrorBoundary } from 'react-error-boundary';
import { APIProvider, Map as GoogleMap, AdvancedMarker, type MapEvent, useMap } from '@vis.gl/react-google-maps';
import { useLocation, useSearchParams } from 'react-router-dom';

import { coordsEqual } from '../lib/utils';
import useStore from '../lib/store';
import type { Bounds, Coords, LocationSummary } from '../lib/consts';
import { groupLocationsByCoords } from '../lib/locationSummaries';
import { useCallback, useEffect, useMemo, useRef, useState } from 'react';

const containerStyle = {
    width: "100%",
    height: "100%",
};

const DEFAULT_CENTER = {
    lat: -8.65,
    lng: 115.22, // Bali
};
const DEFAULT_ZOOM = 12;
const CLUSTER_MAX_ZOOM = 15;
const CLUSTER_GRID_SIZE_PX = 60;
const TILE_SIZE = 256;

interface LocationMarkerPoint {
    key: string;
    name: string;
    aliases: LocationSummary[];
    coords: Coords;
    count: number;
}

interface LocationMarkerCluster {
    key: string;
    coords: Coords;
    count: number;
    points: LocationMarkerPoint[];
}

const projectToWorld = (coords: Coords) => {
    const siny = Math.sin((coords.lat * Math.PI) / 180);
    const clampedSiny = Math.min(Math.max(siny, -0.9999), 0.9999);

    return {
        x: TILE_SIZE * (0.5 + coords.lng / 360),
        y: TILE_SIZE * (0.5 - Math.log((1 + clampedSiny) / (1 - clampedSiny)) / (4 * Math.PI)),
    };
};

const clusterZoomLevel = (zoom: number): number => Math.min(Math.floor(zoom), CLUSTER_MAX_ZOOM);

const buildMarkerClusters = (points: LocationMarkerPoint[], zoomLevel: number): LocationMarkerCluster[] => {
    if (zoomLevel >= CLUSTER_MAX_ZOOM) {
        return points.map(point => ({
            key: point.key,
            coords: point.coords,
            count: point.count,
            points: [point],
        }));
    }

    const pixelScale = 2 ** zoomLevel;
    const clusterMap = new Map<string, LocationMarkerCluster>();

    points.forEach(point => {
        const world = projectToWorld(point.coords);
        const clusterKey = [
            Math.floor((world.x * pixelScale) / CLUSTER_GRID_SIZE_PX),
            Math.floor((world.y * pixelScale) / CLUSTER_GRID_SIZE_PX),
        ].join(':');
        const existing = clusterMap.get(clusterKey);

        if (existing) {
            const nextCount = existing.count + point.count;
            existing.coords = {
                lat: (existing.coords.lat * existing.count + point.coords.lat * point.count) / nextCount,
                lng: (existing.coords.lng * existing.count + point.coords.lng * point.count) / nextCount,
            };
            existing.count = nextCount;
            existing.points.push(point);
        } else {
            clusterMap.set(clusterKey, {
                key: clusterKey,
                coords: point.coords,
                count: point.count,
                points: [point],
            });
        }
    });

    return Array.from(clusterMap.values());
};

const boundsEqual = (a: Bounds | null, b: Bounds, tolerance = 0.00001): boolean => {
    if (!a) return false;

    return Math.abs(a.ne.lat - b.ne.lat) < tolerance
        && Math.abs(a.ne.lng - b.ne.lng) < tolerance
        && Math.abs(a.sw.lat - b.sw.lat) < tolerance
        && Math.abs(a.sw.lng - b.sw.lng) < tolerance;
};

const coordsClose = (a: Coords, b: Coords, tolerance = 0.00001): boolean =>
    Math.abs(a.lat - b.lat) < tolerance && Math.abs(a.lng - b.lng) < tolerance;

const mapOptions = {
    disableDefaultUI: true, // optional: hides zoom buttons etc.
    clickableIcons: false, // optional: disables POI clickability
    gestureHandling: 'cooperative',      // Enables dragging and scroll zoom
    zoomControl: true,            // Optional: show zoom buttons
    draggable: true,              // Ensures dragging is enabled
}

interface MapViewProps {
    locations: Map<string, LocationSummary> | null;
    focusLocation?: string | null;
    useInitialBounds?: boolean;
    suspendUrlSync?: boolean;
    onClearLocation?: () => void;
}

const MapView = ({ locations, focusLocation = null, useInitialBounds = false, suspendUrlSync = false, onClearLocation }: MapViewProps) => {
    const routerLocation = useLocation();
    const searchParams = new URLSearchParams(routerLocation.search);
    const mapsApiKey = import.meta.env.VITE_GOOGLE_MAPS_API_KEY;

    // Read initial viewport from URL (once, on mount)
    const initialCenter = useRef({
        lat: Number(searchParams.get('lat')) || DEFAULT_CENTER.lat,
        lng: Number(searchParams.get('lng')) || DEFAULT_CENTER.lng,
    });
    const initialZoom = useRef(
        Number(searchParams.get('z')) || DEFAULT_ZOOM
    );

    if (!mapsApiKey) {
        return <div className="flex h-full items-center justify-center text-sm text-gray-500">Map unavailable</div>;
    }

    // Keep third-party map failures from replacing listings and rental details.
    return (
        <ErrorBoundary fallback={<div className="flex h-full items-center justify-center text-sm text-gray-500">Map unavailable</div>}>
            <APIProvider apiKey={mapsApiKey}>
                <InnerMapView locations={locations}
                    focusLocation={focusLocation}
                    initialCenter={initialCenter.current}
                    initialZoom={initialZoom.current}
                    useInitialBounds={useInitialBounds}
                    suspendUrlSync={suspendUrlSync}
                    onClearLocation={onClearLocation}
                />
            </APIProvider>
        </ErrorBoundary>
    )
}

// This is here so that we can access the map object using useMap.
interface InnerMapViewProps extends MapViewProps {
    initialCenter: { lat: number; lng: number };
    initialZoom: number;
    useInitialBounds: boolean;
    suspendUrlSync: boolean;
}

const InnerMapView = ({ locations, focusLocation, initialCenter, initialZoom, useInitialBounds, suspendUrlSync, onClearLocation }: InnerMapViewProps) => {
    const { selectedLocation, hoveredLocation, bounds: mapBounds, zoom: storedMapZoom } = useStore(state => state.map);
    const setMap = useStore(state => state.setMap);
    const pendingFocusRef = useRef<{ center: Coords; zoom: number } | null>(null);
    const focusedLocationRef = useRef<string | null>(null);
    const [, setSearchParams] = useSearchParams();
    const viewportTimerRef = useRef<ReturnType<typeof setTimeout> | null>(null);
    const hasHandledInitialIdleRef = useRef(false);
    const suspendUrlSyncRef = useRef(suspendUrlSync);
    const lastPersistedViewportRef = useRef<string | null>(null);
    const [currentClusterZoom, setCurrentClusterZoom] = useState(clusterZoomLevel(initialZoom));

    const map = useMap();

    useEffect(() => {
        suspendUrlSyncRef.current = suspendUrlSync;
        if (suspendUrlSync && viewportTimerRef.current) {
            clearTimeout(viewportTimerRef.current);
            viewportTimerRef.current = null;
        }
    }, [suspendUrlSync]);

    useEffect(() => () => {
        if (viewportTimerRef.current) clearTimeout(viewportTimerRef.current);
    }, []);

    // Recenter the map imperatively when focusLocation changes
    useEffect(() => {
        if (!focusLocation) {
            focusedLocationRef.current = null;
            pendingFocusRef.current = null;
            return;
        }

        const locationKey = focusLocation.toLowerCase();
        if (!map || focusedLocationRef.current === locationKey) return;

        const centerCoords = locations?.get(locationKey);
        if (!centerCoords) return;

        focusedLocationRef.current = locationKey;
        pendingFocusRef.current = { center: centerCoords, zoom: CLUSTER_MAX_ZOOM };
        map.moveCamera({ center: centerCoords, zoom: CLUSTER_MAX_ZOOM });
    }, [focusLocation, locations, map]);

    // Persist viewport to URL (debounced to avoid spamming history)
    const persistViewport = useCallback((map: google.maps.Map) => {
        if (suspendUrlSyncRef.current) return;

        if (viewportTimerRef.current) clearTimeout(viewportTimerRef.current);
        viewportTimerRef.current = setTimeout(() => {
            if (suspendUrlSyncRef.current) return;
            const c = map.getCenter();
            const z = map.getZoom();
            if (!c || z == null) return;
            const lat = c.lat().toFixed(4);
            const lng = c.lng().toFixed(4);
            const zoom = String(z);
            const viewportKey = `${lat}:${lng}:${zoom}`;
            if (lastPersistedViewportRef.current === viewportKey) return;

            const currentParams = new URLSearchParams(window.location.search);
            const urlAlreadyCurrent = currentParams.get('lat') === lat
                && currentParams.get('lng') === lng
                && currentParams.get('z') === zoom;
            if (urlAlreadyCurrent) {
                lastPersistedViewportRef.current = viewportKey;
                return;
            }

            currentParams.set('lat', lat);
            currentParams.set('lng', lng);
            currentParams.set('z', zoom);
            lastPersistedViewportRef.current = viewportKey;
            setSearchParams(currentParams, { replace: true });
        }, 400);
    }, [setSearchParams]);

    const clearSelectedLocation = () => {
        pendingFocusRef.current = null;
        setMap({ selectedLocation: null });
        onClearLocation?.();
    };

    const toggleSelectedLocation = (location: string) => {
        const locationCoords = locations?.get(location.toLowerCase());
        const focusedCoords = focusLocation ? locations?.get(focusLocation.toLowerCase()) : null;
        if (focusLocation && coordsEqual(locationCoords, focusedCoords)) {
            clearSelectedLocation();
            return;
        }

        if (selectedLocation === location) {
            clearSelectedLocation();
            return;
        }

        if (focusLocation) onClearLocation?.();
        setMap({ selectedLocation: location });
    };

    const markerPoints = useMemo(() => {
        const { ne, sw } = mapBounds || {};

        return groupLocationsByCoords(locations?.values())
            .filter(location => !(ne && sw && (location.lat > ne.lat
                || location.lng > ne.lng
                || location.lat < sw.lat
                || location.lng < sw.lng)))
            .map(location => ({
                key: location.key,
                name: location.name,
                aliases: location.aliases,
                coords: { lat: location.lat, lng: location.lng },
                count: location.rentalCount,
            }));
    }, [locations, mapBounds]);

    const markerClusters = useMemo(
        () => buildMarkerClusters(markerPoints, currentClusterZoom),
        [currentClusterZoom, markerPoints]
    );

    const handleMarkerClick = (cluster: LocationMarkerCluster) => {
        if (cluster.points.length === 1) {
            toggleSelectedLocation(cluster.points[0].name);
            return;
        }

        clearSelectedLocation();
        if (!map) return;

        const bounds = new google.maps.LatLngBounds();
        cluster.points.forEach(point => bounds.extend(point.coords));
        map.fitBounds(bounds, {
            bottom: 60,
            left: 60,
            right: 60,
            top: 60,
        });
    };

    const handleOnIdle = (event: MapEvent) => {
        const map = event.map as google.maps.Map;
        if (!map) return;
        const isInitialIdle = !hasHandledInitialIdleRef.current;
        if (isInitialIdle) {
            hasHandledInitialIdleRef.current = true;
        }

        const zoom = map.getZoom();
        if (zoom != null) setCurrentClusterZoom(clusterZoomLevel(zoom));
        const pendingFocus = pendingFocusRef.current;
        const center = map.getCenter();
        const completedPendingFocus = Boolean(pendingFocus
            && zoom === pendingFocus.zoom
            && center
            && coordsClose({ lat: center.lat(), lng: center.lng() }, pendingFocus.center));

        const bounds = map.getBounds();
        if (bounds && (!isInitialIdle || useInitialBounds)) {
            const ne = bounds.getNorthEast();
            const sw = bounds.getSouthWest();
            const nextBounds = { ne: { lat: ne.lat(), lng: ne.lng() }, sw: { lat: sw.lat(), lng: sw.lng() } };
            if (!boundsEqual(mapBounds, nextBounds)) {
                setMap({ bounds: nextBounds, zoom: zoom ?? null });
            } else if (zoom != null && storedMapZoom !== zoom) {
                setMap({ zoom });
            }
            if (pendingFocus) {
                if (completedPendingFocus) pendingFocusRef.current = null;
            }
        }

        if (isInitialIdle) {
            return;
        }

        persistViewport(map);
    };

    const handleZoomChanged = (event: MapEvent) => {
        if (!hasHandledInitialIdleRef.current) return;

        const zoom = event.map.getZoom();
        if (zoom == null) return;

        const pendingFocus = pendingFocusRef.current;
        if (pendingFocus?.zoom === zoom) return;
        if (pendingFocus || selectedLocation || focusLocation) {
            clearSelectedLocation();
        }
    };

    const activeLocation = selectedLocation || focusLocation;
    const selectedCoords = activeLocation ? locations?.get(activeLocation.toLowerCase()) : null;
    const hoveredCoords = hoveredLocation ? locations?.get(hoveredLocation.toLowerCase()) : null;

    return (
        <GoogleMap style={containerStyle} defaultCenter={initialCenter} defaultZoom={initialZoom} mapId="ea226c93475fed38111afbd3"
            onClick={() => clearSelectedLocation()}
            {...mapOptions}
            onDragstart={() => clearSelectedLocation()}
            onIdle={handleOnIdle}
            onZoomChanged={handleZoomChanged}
        >
            {markerClusters.map(cluster => {
                const isCluster = cluster.points.length > 1;
                const isSelected = cluster.points.some(point => coordsEqual(selectedCoords, point.coords));
                const isHovered = cluster.points.some(point => coordsEqual(hoveredCoords, point.coords));
                const count = cluster.count;
                const markerClass = isSelected
                    ? 'bg-orange-600'
                    : isHovered
                        ? 'bg-orange-400'
                        : 'bg-blue-600';

                return (
                    <AdvancedMarker key={cluster.key} position={cluster.coords} onClick={() => handleMarkerClick(cluster)}>
                        <div
                            title={isCluster ? `${count} rentals in this area` : `${count} rentals here`}
                            className={`${markerClass} text-white rounded-full ${isCluster ? 'px-3 py-1.5 ring-2 ring-white/80' : 'px-2 py-1'} text-sm font-medium shadow-md border border-white transition-colors duration-200`}
                        >
                            {count}
                        </div>
                    </AdvancedMarker>
                );
            })}
        </GoogleMap>
    );
};

export default MapView;
